import {
  FormCheckbox,
  FormDatePicker,
  FormFileInput,
  FormInput,
  FormSelect,
} from "@/components";
import { Button } from "@/components/ui";
import { yearOptions } from "@/lib/utils";
import { Bookmark, PencilIcon, Trash2 } from "lucide-react";
import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { TabsDataTypes } from "../types";
import {
  AGREEMENT_DOCUMENT,
  CONTRACT_YEARS,
  DEAL_AMOUNT,
  DEAL_CLOSED_BY,
  DEAL_CLOSURE_DATE,
  EXPECTED_DUE_DATE,
  EXPECTED_LAUNCH_DATE,
  INCLUDES_GST,
  INSTALLMENT,
  INSTALLMENT_AMOUNT,
  INSTALLMENT_NUMBER,
  INSTALLMENT_YEAR,
  LEAD_TYPE,
  leadTypeOptions,
  PAYMENT_FROM_ENTITY,
  pdcOptions,
  PDCS_COLLECTED,
  SALES_PERSON_TYPE,
  salesPersonTypeOptions,
  gstIncludesOptions,
  MOU_AMOUNT,
  INTEREST_PERCENTAGE,
} from "./consts";
import { UpdatePdc } from "./UpdatePdc";
import useInstallmentSchedule from "./useInstallmentSchedule";
import AddNewInstallmentDrawer from "./EditComponents/AddNewInstallmentDrawer";

const InstallmentSchedule: FC<TabsDataTypes> = ({ onNextClick }) => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const {
    form,
    requiredFields,
    onSubmit,
    onCancel,
    attachmentRef,
    onAddNewAcademicInfo,
    onRemoveAcademicInfo,
    scrollRef,
    onCancelUpdatePdc,
    openUpdatePdc,
    onUpdatePdcClick,
    isLoading,
    openAddInstallment,
    onCancelInstallment,
    selectedInstallmentIndex,
    dealClosedByOptions,
  } = useInstallmentSchedule({ onNextClick });
  const { errors } = form.formState;
  const isEdit = action === "edit" && !!id;
  const { control, watch, setValue } = form;

  const installments = watch(INSTALLMENT);

  return (
    <div className="px-5">
      {isLoading && <div className="py-2 text-white">Loading...</div>}

      {/* {!isLoading && ( */}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
          <div className="bg-white rounded-lg pt-3 pb-[100px] px-1">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-[16px] text-primary mb-5">Commercial Info</h2>
              {isEdit && (
                <Button
                  type="submit"
                  variant="default"
                  // disabled={isLoading}
                >
                  Save
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px]">
              <FormDatePicker
                name={DEAL_CLOSURE_DATE}
                control={form.control}
                label="Deal Closure Date"
                placeholder="Please select"
                error={errors[DEAL_CLOSURE_DATE]?.message}
                isRequired={requiredFields.includes(DEAL_CLOSURE_DATE)}
              />
              <FormInput
                name={DEAL_AMOUNT}
                control={form.control}
                label="Deal Amount"
                placeholder="Please enter"
                error={errors[DEAL_AMOUNT]?.message}
                isRequired={requiredFields.includes(DEAL_AMOUNT)}
                variant="integer"
              />
              <FormSelect
                name={INCLUDES_GST}
                control={form.control}
                label="Includes GST?"
                error={errors[INCLUDES_GST]?.message}
                isRequired={requiredFields.includes(INCLUDES_GST)}
                options={gstIncludesOptions}
              />
              <FormSelect
                name={PDCS_COLLECTED}
                control={form.control}
                label="PDCs Collected?"
                placeholder="Please Select"
                error={errors[PDCS_COLLECTED]?.message}
                isRequired={requiredFields.includes(PDCS_COLLECTED)}
                options={pdcOptions}
              />
              <FormSelect
                name={SALES_PERSON_TYPE}
                control={form.control}
                label="Sales Person Type"
                placeholder="Please Select"
                error={errors[SALES_PERSON_TYPE]?.message}
                isRequired={requiredFields.includes(SALES_PERSON_TYPE)}
                options={salesPersonTypeOptions}
              />
              <FormSelect
                name={DEAL_CLOSED_BY}
                control={form.control}
                label="Deal Closed By"
                placeholder="Please Select"
                error={errors[DEAL_CLOSED_BY]?.message}
                isRequired={requiredFields.includes(DEAL_CLOSED_BY)}
                options={dealClosedByOptions}
              />
              <FormSelect
                name={LEAD_TYPE}
                control={form.control}
                label="Lead Type"
                placeholder="Please Select"
                error={errors[LEAD_TYPE]?.message}
                isRequired={requiredFields.includes(LEAD_TYPE)}
                options={leadTypeOptions}
              />
              <FormInput
                name={CONTRACT_YEARS}
                control={form.control}
                label="Contract Years"
                placeholder="Please enter"
                error={errors[CONTRACT_YEARS]?.message}
                isRequired={requiredFields.includes(CONTRACT_YEARS)}
              />
              <FormInput
                name={PAYMENT_FROM_ENTITY}
                control={form.control}
                label="Payment From Entity"
                placeholder="Please enter"
                error={errors[PAYMENT_FROM_ENTITY]?.message}
                isRequired={requiredFields.includes(PAYMENT_FROM_ENTITY)}
              />
              <FormDatePicker
                name={EXPECTED_LAUNCH_DATE}
                control={form.control}
                label="Expected Launch Date"
                placeholder="Please select"
                error={errors[EXPECTED_LAUNCH_DATE]?.message}
                isRequired={requiredFields.includes(EXPECTED_LAUNCH_DATE)}
              />
              <FormFileInput
                fileInputRef={attachmentRef}
                name={AGREEMENT_DOCUMENT}
                control={form.control}
                label="Agreement Document"
                error={errors[AGREEMENT_DOCUMENT]?.message}
                isRequired={requiredFields.includes(AGREEMENT_DOCUMENT)}
                maxSize={5} // in MB
                accept="application/pdf"
              />
            </div>

            <>
              <div className="flex flex-col items-start md:flex-row md:items-center justify-between mb-4 md:mb-auto mt-5">
                <h2 className="text-[16px] text-primary my-5">
                  Academic and Operational Info
                </h2>

                <Button type="button" onClick={onAddNewAcademicInfo}>
                  + Add New
                </Button>
              </div>

              <div
                ref={scrollRef}
                className="grid grid-cols-1 gap-[20px] overflow-y-scroll h-[260px]"
              >
                {form.watch(INSTALLMENT).map((installment, index) => {
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-[25px]">
                      <FormSelect
                        name={`${INSTALLMENT}.${index}.${INSTALLMENT_YEAR}`}
                        control={form.control}
                        label="Year"
                        placeholder="Please select"
                        error={
                          (errors[INSTALLMENT] as any)?.[`${index}`]?.[
                            INSTALLMENT_YEAR
                          ]?.message
                        }
                        isRequired={requiredFields.includes(
                          `${INSTALLMENT}[].${INSTALLMENT_YEAR}`
                        )}
                        options={yearOptions(2023, 2050)}
                        disabled={isEdit}
                      />
                      <FormInput
                        name={`${INSTALLMENT}.${index}.${INSTALLMENT_NUMBER}`}
                        control={form.control}
                        label="Installment #"
                        placeholder="Please enter"
                        error={
                          (errors[INSTALLMENT] as any)?.[`${index}`]?.[
                            INSTALLMENT_NUMBER
                          ]?.message
                        }
                        isRequired={requiredFields.includes(
                          `${INSTALLMENT}[].${INSTALLMENT_NUMBER}`
                        )}
                        disabled={isEdit}
                      />
                      <FormInput
                        name={`${INSTALLMENT}.${index}.${MOU_AMOUNT}`}
                        control={form.control}
                        label="MOU Amount"
                        placeholder="Please enter"
                        prefix="₹"
                        error={
                          (errors[INSTALLMENT] as any)?.[`${index}`]?.[
                            MOU_AMOUNT
                          ]?.message
                        }
                        isRequired={requiredFields.includes(
                          `${INSTALLMENT}[].${MOU_AMOUNT}`
                        )}
                        disabled={isEdit}
                      />
                      <FormInput
                        name={`${INSTALLMENT}.${index}.${INTEREST_PERCENTAGE}`}
                        control={form.control}
                        label="Installment Interest %"
                        placeholder="Please enter"
                        error={
                          (errors[INSTALLMENT] as any)?.[`${index}`]?.[
                            INTEREST_PERCENTAGE
                          ]?.message
                        }
                        isRequired={requiredFields.includes(
                          `${INSTALLMENT}[].${INTEREST_PERCENTAGE}`
                        )}
                        disabled={isEdit}
                      />
                      <FormDatePicker
                        name={`${INSTALLMENT}.${index}.${EXPECTED_DUE_DATE}`}
                        control={form.control}
                        label="Expected Due Date"
                        placeholder="Please select"
                        error={
                          (errors[INSTALLMENT] as any)?.[`${index}`]?.[
                            EXPECTED_DUE_DATE
                          ]?.message
                        }
                        isRequired={requiredFields.includes(
                          `${INSTALLMENT}[].${EXPECTED_DUE_DATE}`
                        )}
                        disabled={isEdit}
                      />
                      <FormInput
                        name={`${INSTALLMENT}.${index}.${INSTALLMENT_AMOUNT}`}
                        control={form.control}
                        label="Installment Amount"
                        placeholder="Please enter"
                        prefix="₹"
                        error={
                          (errors[INSTALLMENT] as any)?.[`${index}`]?.[
                            INSTALLMENT_AMOUNT
                          ]?.message
                        }
                        isRequired={requiredFields.includes(
                          `${INSTALLMENT}[].${INSTALLMENT_AMOUNT}`
                        )}
                        disabled
                      />
                      {isEdit && (
                        <div className="grid grid-cols-2 items-start mx-3">
                          <div className="flex items-center justify-center">
                            <PencilIcon
                              className="stroke-[rgba(70,81,98,1)] cursor-pointer m-3"
                              onClick={() =>
                                navigate(
                                  `/sales-ops/school/${id}/edit/installment/${installment._id}`
                                )
                              }
                            />
                          </div>
                        </div>
                      )}
                      {index != 0 && !isEdit && (
                        <div className="grid grid-cols-2 items-start mx-3">
                          <div className="flex items-center justify-center">
                            <Trash2
                              className="stroke-red-600 h-5 cursor-pointer"
                              onClick={() => onRemoveAcademicInfo(index)}
                            />
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-1 items-center mx-3">
                        {form.watch(PDCS_COLLECTED)?.toLowerCase() == "yes" &&
                        !isEdit ? (
                          <Button
                            variant="link"
                            type="button"
                            onClick={() => onUpdatePdcClick(index)}
                          >
                            Update PDC Info
                          </Button>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          </div>
          {!isEdit && (
            <div className="flex flex-row justify-end gap-3 py-3 absolute bottom-0 w-full bg-white pb-[25px] z-[3]">
              <Button variant="outline" type="button" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit">{"Save & Next"}</Button>
            </div>
          )}
        </form>
      </FormProvider>
      {/* )} */}
      <UpdatePdc
        onCancel={onCancelUpdatePdc}
        open={openUpdatePdc}
        installmentIndex={selectedInstallmentIndex}
        form={form}
      />
      {openAddInstallment && (
        <AddNewInstallmentDrawer
          onCancel={onCancelInstallment}
          open={openAddInstallment}
        />
      )}
    </div>
  );
};

export default InstallmentSchedule;
